import { ArrowLeft, ArrowRight } from "lucide-react";

import { ButtonAnchor } from "@/components/ui/Button";
import { useLocale } from "@/i18n";
import { primaryCtaTarget } from "@/lib/platform-url";

/**
 * The primary call to action.
 *
 * Every "جرّب ذكي" button on the site renders through this, so the question of
 * where it should point is answered in exactly one place. Three separate call
 * sites each deciding for themselves is how one of them gets left pointing at a
 * dead subdomain after a launch.
 *
 * Until `VITE_PLATFORM_LIVE=true`, this is a waitlist link rather than a signup
 * link — because the platform subdomain resolves to nothing, and a visitor who
 * clicks the main button and lands on a browser error is a visitor you have lost
 * and learned nothing from.
 */
export function PrimaryCta({
  size = "lg",
  className,
}: {
  size?: "md" | "lg";
  className?: string;
}) {
  const { t, locale, dir } = useLocale();
  const Arrow = dir === "rtl" ? ArrowLeft : ArrowRight;

  const target = primaryCtaTarget(locale, t.cta.waitlistSubject);

  // Nowhere to send anyone: the platform is not live and no contact address is
  // configured. A disabled button that says so is better than a link that goes
  // nowhere — and it is visible enough that somebody fixes the configuration.
  if (!target) {
    return (
      <span
        className="inline-flex cursor-not-allowed items-center gap-2 rounded-full border border-white/10 px-6 py-3 text-cream-100/40"
        title="VITE_PLATFORM_LIVE / VITE_CONTACT_EMAIL"
      >
        {t.cta.unavailable}
      </span>
    );
  }

  return (
    <ButtonAnchor href={target.href} size={size} className={className}>
      {target.isWaitlist ? t.cta.waitlist : t.cta.primary}
      <Arrow size={18} aria-hidden />
    </ButtonAnchor>
  );
}
